import type { CloudpackConfig } from '@ms-cloudpack/common-types';
import { readConfig, type ReadConfigOptions } from '@ms-cloudpack/config';

export class InvalidCloudpackConfigError extends Error {
  /**
   * If true, only the error message will be shown (no stack trace).
   * Used for expected errors like missing config files.
   */
  isExpected?: boolean;

  constructor(message: string, options?: { isExpected?: boolean }) {
    super(message);
    this.name = 'InvalidCloudpackConfigError';
    this.isExpected = options?.isExpected;
  }
}

/**
 * Read local cloudpack configs for multiple apps, and augment with the given options.
 */
export async function readLocalConfigs(
  appPaths: string[],
  readConfigOptions: ReadConfigOptions,
): Promise<Record<string, CloudpackConfig>> {
  const configs: Record<string, CloudpackConfig> = {};

  for (const appPath of appPaths) {
    let config: CloudpackConfig;

    try {
      config = await readConfig(appPath, readConfigOptions);
    } catch (err) {
      const errorMessage = (err as Error).message || String(err);
      const isExpected = errorMessage.includes('Config file not found:');
      throw new InvalidCloudpackConfigError(`Invalid cloudpack config in ${appPath}: ${errorMessage}`, { isExpected });
    }

    configs[appPath] = config;
  }

  return configs;
}
